import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const LoginForm = ({ mode = 'login' }) => {
  const navigate = useNavigate();
  const { login, resetPassword, updatePassword, loading: authLoading, error: authError } = useAuth();
  
  // Limpar erro quando componente monta
  React.useEffect(() => {
    if (authError) {
      // Pequeno delay para permitir que o usuário veja o erro antes de limpar
      const timer = setTimeout(() => {
        // O erro será limpo automaticamente quando o usuário tentar fazer login novamente
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [authError]);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (mode !== 'reset-password' && !formData?.email) {
      newErrors.email = 'Email é obrigatório';
    } else if (mode !== 'reset-password' && !/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Email inválido';
    }
    
    if (mode === 'login' && !formData?.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (mode === 'login' && formData?.password?.length < 6) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
    }
    
    if (mode === 'reset-password' && !formData?.password) {
      newErrors.password = 'Nova senha é obrigatória';
    } else if (mode === 'reset-password' && formData?.password?.length < 6) {
      newErrors.password = 'Nova senha deve ter pelo menos 6 caracteres';
    }
    
    if (mode === 'reset-password' && !formData?.confirmPassword) {
      newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (mode === 'reset-password' && formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'As senhas não coincidem';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      if (mode === 'login') {
        // Usar função login real do AuthContext
        await login({
          email: formData.email,
          password: formData.password
        });
        
        // Se chegou aqui, login foi bem-sucedido
        console.log('✅ Login realizado com sucesso!');
        
        // Limpar erros - o redirecionamento será feito automaticamente pelo AuthContext
        setErrors({});
      } else if (mode === 'forgot-password') {
        // Usar função resetPassword do AuthContext
        await resetPassword(formData.email);
        setErrors({
          success: 'Email de recuperação enviado! Verifique sua caixa de entrada.'
        });
      } else if (mode === 'reset-password') {
        // Usar função updatePassword do AuthContext
        await updatePassword(formData.password);
        setErrors({
          success: 'Senha atualizada com sucesso! Redirecionando para o login...'
        });
        // Redirecionar para login após 2 segundos
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
      
      // NÃO redirecionar manualmente - o AuthContext fará isso automaticamente
      
    } catch (error) {
      console.error('❌ Erro no login:', error);
      
      // Mensagem amigável sugerindo tentar novamente ou contatar suporte
      setErrors({
        general: mode === 'login' 
          ? 'Não foi possível fazer login. Verifique suas credenciais e tente novamente. Se o problema persistir, entre em contato com o time de suporte.'
          : mode === 'forgot-password'
          ? 'Erro ao enviar email de recuperação. Tente novamente.'
          : 'Erro ao atualizar senha. Tente novamente.'
      });
      
      // Manter usuário na tela de login
      console.log('⚠️ Usuário permanece na tela de login devido ao erro');
      
    } finally {
      setIsLoading(false);
    }
  };

  // Loading total considera loading local + auth loading
  const totalLoading = isLoading || authLoading;

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card border border-border rounded-lg shadow-tier-2 p-8">
        {/* Form Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
            {mode === 'login' ? 'Bem-vindo de volta' : mode === 'forgot-password' ? 'Recuperar senha' : 'Redefinir senha'}
          </h2>
          <p className="text-muted-foreground font-caption">
            {mode === 'login' 
              ? 'Entre na sua conta para continuar aprendendo'
              : mode === 'forgot-password'
              ? 'Digite seu email para receber instruções de recuperação'
              : 'Digite sua nova senha para continuar'
            }
          </p>
        </div>

        {/* General Error */}
        {errors?.general && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <p className="text-sm text-error">{errors?.general}</p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {errors?.success && (
          <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <p className="text-sm text-success">{errors?.success}</p>
            </div>
          </div>
        )}

        {/* Auth Error from AuthContext */}
        {authError && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <p className="text-sm text-error">
                Erro de autenticação: {authError}. Tente novamente ou entre em contato com o time de suporte.
              </p>
            </div>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {mode !== 'reset-password' && (
            <Input
              label="Email"
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleInputChange}
              placeholder="seu@email.com"
              error={errors?.email}
              required
              disabled={totalLoading}
            />
          )}

          {(mode === 'login' || mode === 'reset-password') && (
            <Input
              label={mode === 'login' ? 'Senha' : 'Nova senha'}
              type="password"
              name="password"
              value={formData?.password}
              onChange={handleInputChange}
              placeholder={mode === 'login' ? 'Digite sua senha' : 'Digite sua nova senha'}
              error={errors?.password}
              required
              disabled={totalLoading}
            />
          )}
          
          {mode === 'reset-password' && (
            <Input
              label="Confirmar nova senha"
              type="password"
              name="confirmPassword"
              value={formData?.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirme sua nova senha"
              error={errors?.confirmPassword}
              required
              disabled={totalLoading}
            />
          )}

          {mode === 'login' && (
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-primary border-border rounded focus:ring-ring"
                />
                <span className="text-sm text-muted-foreground font-caption">
                  Lembrar de mim
                </span>
              </label>
              
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-primary hover:text-primary/80 transition-micro"
              >
                Esqueceu a senha?
              </button>
            </div>
          )}

          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={totalLoading}
            disabled={totalLoading}
          >
            {totalLoading 
              ? (mode === 'login' ? 'Entrando...' : mode === 'forgot-password' ? 'Enviando...' : 'Atualizando...') 
              : (mode === 'login' ? 'Entrar' : mode === 'forgot-password' ? 'Enviar email' : 'Atualizar senha')
            }
          </Button>
        </form>

        {mode === 'login' && (
          <>
            {/* Divider */}
            <div className="my-8 flex items-center">
              <div className="flex-1 border-t border-border"></div>
              <span className="px-4 text-sm text-muted-foreground font-caption">ou</span>
              <div className="flex-1 border-t border-border"></div>
            </div>

            {/* Social Login - Temporariamente comentado */}
            {/*
            <div className="space-y-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => handleSocialLogin('google')}
                iconName="Chrome"
                iconPosition="left"
                disabled={totalLoading}
              >
                Continuar com Google
              </Button>
              
              <Button
                variant="outline"
                fullWidth
                onClick={() => handleSocialLogin('facebook')}
                iconName="Facebook"
                iconPosition="left"
                disabled={totalLoading}
              >
                Continuar com Facebook
              </Button>
            </div>
            */}
          </>
        )}

        {/* Sign Up Link / Back to Login Link */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground font-caption">
            {mode === 'login' ? (
              <>
                Não tem uma conta?{' '}
                <button
                  onClick={() => navigate('/registration-screen')}
                  className="text-primary hover:text-primary/80 font-medium transition-micro"
                >
                  Criar conta
                </button>
              </>
            ) : (
              <>
                Lembrou da senha?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-primary hover:text-primary/80 font-medium transition-micro"
                >
                  Voltar ao login
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;